import React, { useState, useEffect } from 'react';
import { promoPackService } from '../../api/promoPackService';
import { productService } from '../../api/productService';
import { uploadService } from '../../api/uploadService';
import Autocomplete from '../Common/Autocomplete';
import '../Common/Common.css';

function PromoPackForm({ promoPack, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    image_url: '',
    description: '',
    promo_price: '',
    fake_original_price: '',
    is_active: true,
  });
  const [items, setItems] = useState([{ product_id: '', quantity: 1 }]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchProducts();
    if (promoPack) {
      setFormData({
        name: promoPack.name || '',
        slug: promoPack.slug || '',
        image_url: promoPack.image_url || '',
        description: promoPack.description || '',
        promo_price: promoPack.promo_price || '',
        fake_original_price: promoPack.fake_original_price || '',
        is_active: promoPack.is_active ?? true,
      });
      if (promoPack.promo_pack_items && promoPack.promo_pack_items.length > 0) {
        setItems(
          promoPack.promo_pack_items.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
          }))
        );
      }
      setImagePreview(promoPack.image_url || '');
    }
  }, [promoPack]);

  const fetchProducts = async () => {
    try {
      // Load the full catalog so every product is selectable in the picker
      // (the searchable Autocomplete below filters this list client-side).
      const response = await productService.getProducts({ per_page: 1000 });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSlugGenerate = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData({ ...formData, slug });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;

    try {
      setUploadingImage(true);
      const uploadedUrl = await uploadService.uploadImage(imageFile, 'promo-packs');
      setFormData({ ...formData, image_url: uploadedUrl });
      setImageFile(null);
      alert('Image téléchargée avec succès !');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Échec du téléchargement de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1 }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      alert('Le pack promo doit avoir au moins un produit');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.slug) {
      alert('Veuillez remplir le nom et le slug');
      return;
    }

    const validItems = items.filter((item) => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      alert('Veuillez ajouter au moins un produit au pack promo');
      return;
    }

    setLoading(true);
    try {
      // If the user picked an image but never clicked "Télécharger l'image",
      // upload it now so the pack is saved WITH its image (otherwise image_url
      // stays empty and the pack shows no picture on the storefront).
      let imageUrl = formData.image_url;
      if (imageFile) {
        try {
          setUploadingImage(true);
          imageUrl = await uploadService.uploadImage(imageFile, 'promo-packs');
          setFormData((prev) => ({ ...prev, image_url: imageUrl }));
          setImageFile(null);
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Échec du téléchargement de l\'image');
          return; // abort save — don't persist a pack without its picture
        } finally {
          setUploadingImage(false);
        }
      }

      const dataToSend = {
        ...formData,
        image_url: imageUrl,
        items: validItems,
      };

      if (promoPack) {
        await promoPackService.updatePromoPack(promoPack.id, dataToSend);
      } else {
        await promoPackService.createPromoPack(dataToSend);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving promo pack:', error);
      alert(error.response?.data?.message || 'Échec de l\'enregistrement du pack promo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="form-group">
        <label htmlFor="name">Nom *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="form-control"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="slug">Slug *</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            className="form-control"
            required
          />
          <button type="button" onClick={handleSlugGenerate} className="btn btn-secondary">
            Générer
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="image">Image</label>
        <div style={{ marginBottom: '10px' }}>
          <input
            type="file"
            id="image"
            accept="image/*"
            onChange={handleImageSelect}
            className="form-control"
          />
          {imageFile && (
            <button
              type="button"
              onClick={handleImageUpload}
              disabled={uploadingImage}
              className="btn btn-secondary"
              style={{ marginTop: '10px' }}
            >
              {uploadingImage ? 'Téléchargement...' : 'Télécharger l\'image'}
            </button>
          )}
        </div>
        <label htmlFor="image_url">Ou coller l'URL de l'image</label>
        <input
          type="url"
          id="image_url"
          name="image_url"
          value={formData.image_url}
          onChange={handleChange}
          className="form-control"
          placeholder="https://example.com/image.jpg"
        />
        {imagePreview && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={imagePreview}
              alt="Aperçu"
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows="3"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="promo_price">Prix promo *</label>
          <input
            type="number"
            id="promo_price"
            name="promo_price"
            value={formData.promo_price}
            onChange={handleChange}
            className="form-control"
            step="0.01"
            min="0"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="fake_original_price">Prix original factice</label>
          <input
            type="number"
            id="fake_original_price"
            name="fake_original_price"
            value={formData.fake_original_price}
            onChange={handleChange}
            className="form-control"
            step="0.01"
            min="0"
          />
          <small>Pour afficher la réduction</small>
        </div>
      </div>

      <div className="form-group">
        <label>Produits dans le pack *</label>
        {items.map((item, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 2 }}>
              <Autocomplete
                options={products.map((p) => ({ id: p.id, displayName: p.name }))}
                value={item.product_id}
                onChange={(value) => handleItemChange(index, 'product_id', value)}
                placeholder="Rechercher un produit..."
                displayKey="displayName"
                valueKey="id"
              />
            </div>
            <input
              type="number"
              value={item.quantity}
              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
              className="form-control"
              style={{ flex: 1 }}
              min="1"
              placeholder="Qté"
              required
            />
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="btn btn-danger"
              disabled={items.length === 1}
            >
              Supprimer
            </button>
          </div>
        ))}
        <button type="button" onClick={addItem} className="btn btn-secondary">
          Ajouter un produit
        </button>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <span>Actif</span>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Enregistrement...' : promoPack ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
}

export default PromoPackForm;
